import { isNumber } from "./text";

// test for 5+ trailing 0s or 9s then 1-4 other digits
const NUMERIC_NOISE = /^\s*(-?(\d*\.(\d*?)(?:0{5,}|9{5,})\d{1,4}))\s*$/;

/**
 * Trims bad floating point string representations
 *
 * Example: assert(trimExcessSignificance(1.3 - 1), "0.3")
 */
export function trimExcessSignificance(num: number | string) {
  const str = num.toString();

  return str.replace(
    NUMERIC_NOISE,
    (_, originalValue, originalAbsoluteValue, trimmedFraction) =>
      originalAbsoluteValue.length > 15
        ? Number(originalValue).toFixed(trimmedFraction.length)
        : originalValue,
  );
}

// Constants for when to swap from normal to scientific notation
export const SCIENTIFIC_LOW = -5;
export const SCIENTIFIC_HIGH = 5;

export function roundValue(
  value?: number | string,
  forceDirection?: 1 | -1,
): string {
  if (
    value === undefined ||
    value === 0 ||
    typeof value === "string" ||
    !isNumber(value)
  ) {
    return "0";
  }

  const lead = Math.floor(Math.log10(Math.abs(value)));
  const isExponential = lead < SCIENTIFIC_LOW || lead > SCIENTIFIC_HIGH;
  const fractionDigits = isExponential ? 2 : Math.max(0, 1 - lead);

  if (forceDirection) {
    const roundBase = isExponential
      ? Math.pow(10, 2 - lead)
      : Math.pow(10, fractionDigits);

    // shift the decimal place to apply ceil/floor
    value *= roundBase;
    value = forceDirection > 0 ? Math.ceil(value) : Math.floor(value);
    // unshift the decimal place after applying ceil/floor
    value /= roundBase;

    // shifting/unshifting the decimal place can introduce floating point error
    value = Number(trimExcessSignificance(value));
  }

  return isExponential
    ? value.toExponential(fractionDigits)
    : value.toFixed(fractionDigits);
}
